var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { withRetry, withTimeout } from '../utils';
// API configuration
const API_TIMEOUT_MS = 10000; // 10 seconds for API calls
const API_RETRY_ATTEMPTS = 3; // Up to 3 attempts
const API_INITIAL_DELAY_MS = 1000; // 1 second initial delay
// Bid endpoint runs an upstream auction (AY PBS) with its own 1.5s timeout;
// give the SDK a slightly longer ceiling to cover the network hop. No retries —
// a missed bid window is better than a stale bid against a refreshed slot.
const BID_TIMEOUT_MS = 2000;
const NO_BID = { hasBid: false, targeting: {} };
/**
 * API Client for communicating with the Ads Control Plane backend
 * Includes automatic retry with exponential backoff and timeout protection
 */
export class ApiClient {
    constructor(baseUrl) {
        this.baseUrl = baseUrl;
    }
    /**
     * Execute a fetch request with retry and timeout
     */
    fetchWithRetry(url, options, operationName) {
        return __awaiter(this, void 0, void 0, function* () {
            return withRetry(() => __awaiter(this, void 0, void 0, function* () {
                return withTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    const response = yield fetch(url, options);
                    if (!response.ok) {
                        throw new Error(`${operationName} failed: ${response.status} ${response.statusText}`);
                    }
                    return response.json();
                }), API_TIMEOUT_MS, `${operationName} timed out after ${API_TIMEOUT_MS}ms`);
            }), {
                maxAttempts: API_RETRY_ATTEMPTS,
                initialDelayMs: API_INITIAL_DELAY_MS,
                onRetry: (attempt, error) => {
                    console.warn(`[GetJarAds] ${operationName} attempt ${attempt} failed, retrying...`, error.message);
                },
            });
        });
    }
    /**
     * Initialize session with backend
     */
    init(appId, publisherId, context) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetchWithRetry(`${this.baseUrl}/ads/init`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    appId,
                    publisherId,
                    context,
                }),
            }, 'Init');
        });
    }
    /**
     * Send event to backend
     */
    sendEvent(sessionId, eventType, eventData) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetchWithRetry(`${this.baseUrl}/ads/events`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    eventType,
                    eventData,
                }),
            }, 'Event');
        });
    }
    /**
     * Request a server-side prebid auction (AY S2S). Only invoke when the init
     * response declared `adProvider === 'ay'`.
     *
     * Fail-open: any network error, non-2xx response, timeout, or malformed body
     * resolves with `{ hasBid: false, targeting: {} }` so the caller can proceed
     * with a direct GAM auction. We don't retry — the slot is about to fetch and
     * a retried bid would race the GPT request.
     */
    requestBids(req) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield withTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    const response = yield fetch(`${this.baseUrl}/ads/bid`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(req),
                    });
                    if (!response.ok)
                        return NO_BID;
                    const body = (yield response.json());
                    if (!body || typeof body !== 'object')
                        return NO_BID;
                    return {
                        hasBid: body.hasBid === true,
                        targeting: body.targeting && typeof body.targeting === 'object' ? body.targeting : {},
                        bidder: body.bidder,
                        cpm: body.cpm,
                        currency: body.currency,
                    };
                }), BID_TIMEOUT_MS, `Bid request timed out after ${BID_TIMEOUT_MS}ms`);
            }
            catch (err) {
                console.warn('[GetJarAds] Bid request failed; proceeding without prebid targeting:', err);
                return NO_BID;
            }
        });
    }
    /**
     * Exchange an AdScore JS-API signature for a verdict. The backend decodes
     * the signature with its server-only validation key and caches the verdict
     * on the session (once-per-session — subsequent calls short-circuit).
     *
     * Fail-open: any error → `{ verdict: null }`. The SDK treats null the same
     * as "AdScore off" and never blocks on our own verification failure.
     */
    verifyAdscore(sessionId, signature) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch(`${this.baseUrl}/ads/adscore/verify`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId, signature }),
                });
                if (!response.ok)
                    return { verdict: null };
                const body = (yield response.json());
                return { verdict: typeof (body === null || body === void 0 ? void 0 : body.verdict) === 'string' ? body.verdict : null };
            }
            catch (_a) {
                return { verdict: null };
            }
        });
    }
    /**
     * Validate reward with backend
     */
    validateReward(sessionId, rewardType, context) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetchWithRetry(`${this.baseUrl}/ads/reward/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    rewardType,
                    context,
                }),
            }, 'Reward validation');
        });
    }
}
